import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpBackend from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';

import { config } from './lib/config';
import { rollbar } from './lib/rollbar';

export const Language = {
    German: 'de-CH',
    French: 'fr-CH',
    Italian: 'it-CH',
    English: 'en-CH',
} as const;
export type Language = (typeof Language)[keyof typeof Language];

export const TranslationsNamespaces = {
    PartnerCockpit: 'partner-cockpit',
} as const;
export type TranslationsNamespaces = (typeof TranslationsNamespaces)[keyof typeof TranslationsNamespaces];

export const SUPPORTED_LANGUAGES = [Language.German, Language.French, Language.Italian, Language.English] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const DEFAULT_LANGUAGE: SupportedLanguage = Language.German;

export function isSupportedLanguage(value: string): value is SupportedLanguage {
    return (SUPPORTED_LANGUAGES as ReadonlyArray<string>).includes(value);
}

// The language actually shown in the UI.
export function getActiveLanguage(): string {
    return i18n.resolvedLanguage ?? i18n.language;
}

const COMMON_INIT_OPTIONS = {
    supportedLngs: [...SUPPORTED_LANGUAGES],
    // We only ship region-specific bundles (de-CH, fr-CH, …). Without this,
    // i18next also resolves the base code (de, fr, …), which isn't in
    // supportedLngs — spamming "rejecting language code" warnings and 404ing
    // the backend for `/translations/<ns>/de`.
    load: 'currentOnly',
    ns: [TranslationsNamespaces.PartnerCockpit],
    defaultNS: TranslationsNamespaces.PartnerCockpit,
    fallbackLng: DEFAULT_LANGUAGE,
    keySeparator: false,
    nsSeparator: false,
    returnEmptyString: false,
    interpolation: { escapeValue: false },
} as const;

function attachListeners(): void {
    if (typeof document !== 'undefined') {
        i18n.on('languageChanged', (lng) => {
            document.documentElement.lang = lng;
        });
    }

    // De-dupe so a single missing key doesn't spam Rollbar on every render.
    const reportedMissingKeys = new Set<string>();
    i18n.on('missingKey', (lngs, ns, key) => {
        const cacheKey = `${ns}:${key}`;
        if (reportedMissingKeys.has(cacheKey)) {
            return;
        }
        reportedMissingKeys.add(cacheKey);
        rollbar.warning('i18n missing translation key', { ns, key, lngs });
    });
}

export function initI18n(): Promise<unknown> {
    if (i18n.isInitialized) {
        return Promise.resolve();
    }

    if (!config.apiUrl && config.isProductionMode) {
        // oxlint-disable-next-line no-console
        console.warn('Missing VITE_API_URL. Translations will not load in production.');
    }

    const initPromise = i18n
        .use(HttpBackend)
        .use(LanguageDetector)
        .use(initReactI18next)
        .init({
            ...COMMON_INIT_OPTIONS,
            debug: !config.isProductionMode,
            backend: {
                loadPath: `${config.apiUrl}/translations/{{ns}}/{{lng}}`,
            },
            detection: {
                order: ['path', 'localStorage', 'navigator', 'htmlTag'],
                caches: ['localStorage'],
                lookupFromPathIndex: 0,
                lookupLocalStorage: 'partner-cockpit.language',
                // Map "en" to "en-CH" and so on.
                convertDetectedLanguage: (lng: string) => {
                    switch (lng) {
                        case 'en':
                            return Language.English;
                        case 'de':
                            return Language.German;
                        case 'fr':
                            return Language.French;
                        case 'it':
                            return Language.Italian;
                        default:
                            return lng;
                    }
                },
            },
            react: { useSuspense: true },
            // Needed to emit the 'missingKey' event that attachListeners hooks for Rollbar.
            // No backend addPath is called because we provide missingKeyHandler ourselves.
            saveMissing: true,
            missingKeyHandler: () => {
                // handled via the event listener below
            },
        });

    attachListeners();
    return initPromise;
}

// Storybook drives the language via the toolbar (no path/localStorage detection),
// the HTTP backend is intercepted by MSW, and Suspense is off so stories render
// the fallback chain instead of flashing a loading state.
export function initI18nForStorybook(): Promise<unknown> {
    if (i18n.isInitialized) {
        return Promise.resolve();
    }

    const initPromise = i18n
        .use(HttpBackend)
        .use(initReactI18next)
        .init({
            ...COMMON_INIT_OPTIONS,
            lng: DEFAULT_LANGUAGE,
            debug: false,
            backend: {
                loadPath: `/translations/{{ns}}/{{lng}}`,
            },
            react: { useSuspense: false },
        });

    attachListeners();
    return initPromise;
}

export function initI18nForTests(resources?: Record<string, Record<string, Record<string, string>>>): Promise<unknown> {
    if (i18n.isInitialized) {
        return Promise.resolve();
    }

    return i18n.use(initReactI18next).init({
        ...COMMON_INIT_OPTIONS,
        lng: DEFAULT_LANGUAGE,
        debug: false,
        resources,
        react: { useSuspense: false },
    });
}

export { i18n };
